import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { RiskAssessmentResultsByRiskIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetRiskAssessmentResultsByRiskIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

const mapTrpcRiskAssessmentResultsToGraphQL = (
  trpcData: RiskAssessmentResultsByRiskIdResponseRow[]
): GetRiskAssessmentResultsByRiskIdQuery => {
  return {
    risk_assessment_result: trpcData,
  };
};

export const useGetRiskAssessmentResultsByRiskId = (
  riskId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.assessment.riskAssessmentResultsByRiskId.queryOptions({
      riskId: riskId,
    }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcError, addNotification, trpcEnabled]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcRiskAssessmentResultsToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () =>
      mapTRPCRefetch(trpcRefetch, mapTrpcRiskAssessmentResultsToGraphQL),
    error: trpcError,
  };
};
